import { useState } from 'react';
import { Select } from './Select.js';
import { Icon } from './ui/Icon.js';
import type { CollectionRun } from '../types/index.js';

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return 'never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const cadenceOptions = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'daily', label: 'Daily' },
  { value: 'manual', label: 'Manual' },
];

interface CollectionStatusProps {
  /** Spec 124 — the selected brand's most-recent run bucket (history[0]). */
  latestRun: CollectionRun | null;
  /** Spec 124 — true while this brand has a collection actually executing. */
  running: boolean;
}

/**
 * Spec 124 — status is now BRAND-SCOPED and derived from real collection data,
 * not the global in-process scheduler (`/admin/scheduler/status`), whose
 * `running` meant "weekly cadence armed" and whose `lastRun` reset to null on
 * every Scaleway cold-start (→ a false "never"). `running` reflects whether a
 * collection is executing for the brand; "Last run" is the brand's most recent
 * run from history.
 */
export function CollectionStatus({ latestRun, running }: CollectionStatusProps) {
  const [cadence, setCadence] = useState('weekly');

  return (
    <div className="flex flex-wrap items-center gap-4 hub-plate--flat px-4 py-2.5" data-testid="collection-status">
      <div className="flex items-center gap-1.5">
        <Icon name="bx-pulse" className={running ? 'text-green-500' : 'text-gray-400'} />
        <span className="text-xs font-medium text-slate-600">
          {running ? 'Running' : 'Idle'}
        </span>
      </div>

      <div className="flex items-center gap-1.5">
        <Icon name="bx-time-five" className="text-slate-400" />
        <span className="text-xs text-slate-500">Last run: {timeAgo(latestRun?.triggered_at ?? null)}</span>
      </div>

      {latestRun && (
        <span className="text-xs text-slate-500">
          {latestRun.jobs_created} jobs
        </span>
      )}

      <div className="flex items-center gap-1.5 ml-auto">
        <span className="text-xs text-slate-500">Cadence:</span>
        <Select
          options={cadenceOptions}
          value={cadence}
          onChange={setCadence}
          className="w-28"
          data-testid="cadence-select"
        />
      </div>
    </div>
  );
}
